import React, { useState, useMemo } from "react"; 
import { useNavigate } from "react-router-dom";
import T from '../../components/common/T'
import DataTable from "../../components/common/DataTables.jsx";
import PageHeader from "../../components/common/PageHeader";

// === CONTEXT IMPORTS ===
import { useProjects } from "../../context/ProjectContext";
import { useBudgets } from "../../context/BudgetContext"; 
import { useTasks } from "../../context/TaskContext"; 
import { usePurchaseOrder } from "../../context/PurchaseOrderContext"; 
import { useTimesheets } from "../../context/TimesheetContext"; 
import { useConsumption } from "../../context/ConsumptionContext"; 
import { useEmployees } from "../../context/EmployeeContext";     
import { useCostSheets } from "../../context/CostSheetContext";   
import { useEquipment } from "../../context/EquipmentContext";    
import { useAuth } from "../../context/AuthContext"; 
import Price from "../../components/common/Price";

import { toast } from "react-toastify";
import TableActionButtons from "../../components/common/TableActionButtons";
import { BanknotesIcon, TrashIcon } from "@heroicons/react/24/outline";
import { formatDate } from "../../utils/dateUtils";

const ProjectListView = ({ projects }) => {
  const navigate = useNavigate();
  const { deleteProject } = useProjects();
  const { user } = useAuth(); 

  const { budgets = [] } = useBudgets() || {}; 
  const { tasks = [] } = useTasks() || {}; 
  const { purchaseOrders = [] } = usePurchaseOrder() || {}; 
  const { timesheets = [] } = useTimesheets() || {}; 
  const { issuances = [] } = useConsumption() || {};
  const { employees = [] } = useEmployees() || {};
  const { costSheets = [] } = useCostSheets() || {};
  const { usageLogs = [] } = useEquipment() || {};

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState(null);

  const userRole = user?.role?.toLowerCase() || ""; 
  const canEdit = userRole === "admin" || userRole === "super admin" || userRole === "manager";
  const canDelete = userRole === "admin" || userRole === "super admin";

  const validProjects = projects?.filter(p => p.name && p.name.trim() !== "") || [];

  const projectFinancials = useMemo(() => {
    const financialsMap = {};
    validProjects.forEach(project => {
        const projectSheetIds = (costSheets || []).filter(sheet => String(sheet.projectId) === String(project.id)).map(sheet => sheet.id);
        const projectTaskIds = new Set((tasks || []).filter(t => String(t.projectId) === String(project.id)).map(t => String(t.id)));
        
        const activePOStatuses = ["Ordered", "Dispatched", "Received", "QC Pending", "Completed", "Delivered"];
        const poCost = (purchaseOrders || [])
            .filter(po => (String(po.projectId) === String(project.id) || po.projectName === project.name) && activePOStatuses.includes(po.status))
            .reduce((sum, po) => sum + (Number(po.totalPrice) || 0), 0);

        const materialCost = (issuances || []).filter(issue => String(issue.projectId) === String(project.id)).reduce((sum, issue) => sum + (Number(issue.quantity) * Number(issue.unitPrice || 0)), 0);
        
        const laborCost = (timesheets || []).filter(log => projectTaskIds.has(String(log.taskId)) || String(log.projectId) === String(project.id) || projectSheetIds.includes(log.costSheetId)).reduce((sum, log) => {
                const cost = log.cost ? Number(log.cost) : 0;
                if (cost > 0) return sum + cost;
                const emp = (employees || []).find(e => String(e.id) === String(log.employeeId));
                return sum + (Number(log.hours) * Number(emp?.hourlyRate || 0));
            }, 0);

        const equipmentCost = (usageLogs || []).filter(log => projectSheetIds.includes(log.costSheetId)).reduce((sum, log) => sum + (Number(log.totalCost) || 0), 0);
        const totalSpent = poCost + materialCost + laborCost + equipmentCost;

        const dbBudget = budgets.find(b => String(b.projectId) === String(project.id));
        let finalBudget = dbBudget ? ((Number(dbBudget.laborCostEstimate) || 0) + (Number(dbBudget.materialCostEstimate) || 0) + (Number(dbBudget.equipmentCostEstimate) || 0) + (Number(dbBudget.realCostEstimate) || 0)) : (Number(project.budget) || 0);

        financialsMap[project.id] = { budget: finalBudget, spent: totalSpent, isOverBudget: totalSpent > finalBudget, hasDbBudget: !!dbBudget };
    });
    return financialsMap;
  }, [validProjects, budgets, tasks, purchaseOrders, timesheets, issuances, employees, costSheets, usageLogs]);

  const getProgressStats = (projectId) => {
    const pTasks = tasks.filter(t => String(t.projectId) === String(projectId));
    const totalTasks = pTasks.length;
    const completedTasks = pTasks.filter(t => t.status === "Completed").length;
    const totalEstimatedHours = pTasks.reduce((sum, t) => sum + (Number(t.estimatedHours) || 0), 0);
    const totalSpentHours = timesheets.filter(log => String(log.projectId) === String(projectId)).reduce((sum, log) => sum + (Number(log.hours) || 0), 0);

    let progressPercentage = 0;
    if (totalEstimatedHours > 0) progressPercentage = Math.min(Math.round((totalSpentHours / totalEstimatedHours) * 100), 100);
    else if (totalTasks > 0) progressPercentage = Math.round((completedTasks / totalTasks) * 100);
    
    return { progressPercentage, totalSpentHours, totalEstimatedHours, completedTasks, totalTasks };
  };

  const getStatusBadge = (status) => {
    let styles = "bg-gray-100 dark:bg-slate-800 text-gray-700 dark:text-slate-300 border-gray-200 dark:border-slate-700";
    let dotColor = "bg-gray-500";
    switch (status) {
      case "Completed": styles = "bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800/60"; dotColor = "bg-emerald-500"; break;
      case "In Progress": styles = "bg-blue-50 dark:bg-blue-950/40 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-800/60"; dotColor = "bg-blue-500"; break;
      case "Pending": styles = "bg-amber-50 dark:bg-amber-950/40 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-800/60"; dotColor = "bg-amber-500"; break;
      case "On Hold": styles = "bg-rose-50 dark:bg-rose-950/40 text-rose-700 dark:text-rose-300 border-rose-200 dark:border-rose-800/60"; dotColor = "bg-rose-500"; break;
    }
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide border ${styles}`}>
        <span className={`w-1.5 h-1.5 rounded-full ${dotColor}`}></span>{status}
      </span>
    );
  };

  const columns = [
    {
      header: "Project Name",
      accessor: "name",
      sortable: true,
      render: (p) => {
        const hasDates = Boolean(p.startDate || p.endDate);
        return (
          <div>
            <button onClick={() => navigate(`/admin/projects/view/${p.id}`)} className="text-gray-900 dark:text-slate-100 font-bold hover:text-indigo-600 dark:hover:text-indigo-400 hover:underline text-left block">
              {p.name}
            </button>
            {hasDates && (
              <div className="text-[10px] text-gray-400 dark:text-slate-500 mt-0.5 flex items-center gap-1">
                <span>{formatDate(p.startDate)}</span>
                {p.startDate && p.endDate && <span>—</span>}
                <span>{formatDate(p.endDate)}</span>
              </div>
            )}
          </div>
        );
      }
    },
    { header: "Client", accessor: "client", sortable: true, render: (p) => <span className="font-medium text-gray-700 dark:text-slate-300">{p.client || "—"}</span> },
    { header: "Status", accessor: "status", align: "center", sortable: true, render: (p) => getStatusBadge(p.status) },
    {
      header: "Progress",
      render: (p) => {
        const progress = getProgressStats(p.id);
        return (
          <div className="min-w-[140px]">
            <div className="flex justify-between text-[10px] mb-1">
              <span className="font-medium text-gray-500 dark:text-slate-400">
                {progress.totalEstimatedHours > 0 ? `${progress.totalSpentHours} / ${progress.totalEstimatedHours} Hrs` : `${progress.completedTasks}/${progress.totalTasks} Tasks`}
              </span>
              <span className={`font-bold ${progress.progressPercentage === 100 ? "text-emerald-600 dark:text-emerald-400" : "text-indigo-600 dark:text-indigo-400"}`}>{progress.progressPercentage}%</span>
            </div>
            <div className="w-full bg-gray-100 dark:bg-slate-800 rounded-full h-1.5 shadow-inner overflow-hidden">
              <div className={`h-full rounded-full transition-all duration-700 ease-out ${progress.progressPercentage >= 100 ? "bg-emerald-500" : "bg-indigo-500"}`} style={{ width: `${progress.progressPercentage}%` }}></div>
            </div>
          </div>
        );
      }
    },
    {
      header: "Financials",
      render: (p) => {
        const financials = projectFinancials[p.id] || { budget: 0, spent: 0, isOverBudget: false };
        return (
          <div>
            <div className="text-[10px] text-gray-500 dark:text-slate-400 flex items-center gap-1">
              Budget: <span className="font-semibold text-gray-900 dark:text-slate-200"><Price amount={financials.budget} /></span>
              {financials.hasDbBudget && <BanknotesIcon className="w-3 h-3 text-indigo-500" title="Strict Budget Active" />}
            </div>
            <div className="text-[10px] text-gray-500 dark:text-slate-400 mt-0.5">
              Spent: <span className={`font-bold ${financials.isOverBudget ? "text-red-600 dark:text-red-400" : "text-gray-700 dark:text-slate-300"}`}><Price amount={financials.spent} /></span>
            </div>
          </div>
        );
      }
    },
    {
      header: "Actions",
      align: "right",
      render: (p) => (
        <TableActionButtons
          onView={() => navigate(`/admin/projects/view/${p.id}`)}
          onEdit={canEdit ? () => navigate(`/admin/project/${p.id}`) : undefined}
          onDelete={canDelete ? () => { setProjectToDelete({ id: p.id, name: p.name }); setIsDeleteModalOpen(true); } : undefined}
        />
      )
    }
  ];

  return (
    <>
      <div className="relative">
        <DataTable data={validProjects} columns={columns} tableId="projects" emptyMessage="No projects found. Create one to get started!" />
      </div>

      {isDeleteModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-xs p-4 animate-fade-in">
          <div className="bg-white dark:bg-[#161b22] border border-gray-200 dark:border-[#30363d] rounded-xl shadow-2xl w-full max-w-md overflow-hidden transform transition-all scale-100">
            <div className="flex flex-col items-center pt-8 px-6">
              <div className="w-16 h-16 bg-red-100 dark:bg-red-950/50 rounded-full flex items-center justify-center mb-4 shadow-xs">
                <TrashIcon className="h-8 w-8 text-red-600 dark:text-red-400" />
              </div>
              <h3 className="text-base font-bold text-gray-900 dark:text-white text-center"><T>Delete Project?</T></h3>
              <p className="text-gray-500 dark:text-slate-400 text-sm text-center mt-2">
                Are you sure you want to permanently delete <span className="font-bold text-gray-800 dark:text-slate-200">"{projectToDelete?.name}"</span>?
                <br />This action cannot be undone.
              </p>
            </div>
            <div className="flex items-center gap-3 p-6 mt-2">
              <button onClick={() => setIsDeleteModalOpen(false)} className="flex-1 px-4 py-2 bg-gray-50 dark:bg-slate-800 border border-gray-300 dark:border-[#30363d] text-gray-700 dark:text-slate-300 font-semibold rounded hover:bg-gray-100 dark:hover:bg-slate-700 transition duration-200 text-sm"><T>Cancel</T></button>
              <button onClick={() => {
                if (projectToDelete) {
                  deleteProject(projectToDelete.id);
                  toast.success(`"${projectToDelete.name}" deleted successfully!`);
                  setIsDeleteModalOpen(false);
                  setProjectToDelete(null);
                }
              }} className="flex-1 px-4 py-2 bg-red-600 text-white font-semibold rounded hover:bg-red-700 shadow-md transition duration-200 text-sm"><T>Yes, Delete It</T></button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ProjectListView;